import {
    DeepIntelligenceConfigurationModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { colorForAgentId } from '@/lib/deepIntelligenceAvatar';
import { ArrowLeft, Plus, Search } from 'lucide-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ChainlitContext } from '@chainlit/react-client';

import { cronToHuman } from '@/components/DMAdminComponents/CronPicker';
import {
    BankCard,
    DisplayBankAgent
} from '@/components/ScheduledAgents/BankCard';
import { MobileNavTrigger } from '@/components/ScheduledAgents/MobileNavTrigger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

import { useAuth } from '@/hooks/useAuth';
import { useIsBasicUser } from '@/hooks/useIsBasicUser';

function liveToDisplay(
    config: DeepIntelligenceConfigurationModel,
    isOwner: boolean,
    subscriberCount: number,
    fallbackOwnerName?: string
): DisplayBankAgent {
    const schedule = config.cronExpression
        ? cronToHuman(config.cronExpression)
        : '—';
    // Configs saved before this field existed fall back to the
    // batch-resolved lookup by chatUserId.
    const ownerDisplayName = config.ownerDisplayName || fallbackOwnerName;
    return {
        id: config.id,
        name: config.displayName || '—',
        author: ownerDisplayName || '—',
        // Keyed by the job's own id, not managedAgentId — matches the Feed's
        // color key (thread.metadata.config_id) so a job's color stays
        // consistent between the two pages even when several jobs share one
        // underlying managed agent.
        avatarColor: colorForAgentId(config.id),
        frequency: schedule,
        format: config.isActive ? 'Active' : 'Inactive',
        subscribers: String(subscriberCount),
        blurb: config.description || config.prompt || '—',
        isOwner,
        isPublic: config.isPublic
    };
}

const FILTERS = ['All', 'Subscribed', 'Mine'] as const;

type Filter = (typeof FILTERS)[number];

// Matches the pill styling introduced for the Feed's JobFilterControl so the
// two pages read as one system.
export default function AgentBank() {
    const navigate = useNavigate();
    const isBasicUser = useIsBasicUser();
    const { user } = useAuth();
    const apiClient = useContext(ChainlitContext);
    const [subscribed, setSubscribed] = useState<Set<string>>(new Set());
    const [subscriberCounts, setSubscriberCounts] = useState<
        Record<string, number>
    >({});
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState<Filter>('All');

    const [liveConfigs, setLiveConfigs] = useState<
        DeepIntelligenceConfigurationModel[]
    >([]);
    const [liveLoading, setLiveLoading] = useState(false);
    const [ownerNames, setOwnerNames] = useState<Record<string, string>>({});

    const mgmtApi = useMemo(
        () =>
            new MgmtApi({
                baseUrl: '/mgmt',
                baseApiParams: { credentials: 'include' }
            }),
        []
    );

    useEffect(() => {
        setLiveLoading(true);
        Promise.all([
            mgmtApi.api.v1DeepIntelligenceConfigurationList(),
            apiClient
                .get('/project/deep-intelligence/subscriptions')
                .then((res) => res.json())
                .catch(() => ({ configIds: [] })),
            apiClient
                .get('/project/deep-intelligence/subscriptions/counts')
                .then((res) => res.json())
                .catch(() => ({ counts: {} }))
        ])
            .then(([configsRes, subsRes, countsRes]) => {
                const configs = configsRes?.data || [];
                setLiveConfigs(configs);
                setSubscribed(new Set(subsRes?.configIds || []));
                setSubscriberCounts(countsRes?.counts || {});

                // Fallback only — configs saved after ownerDisplayName was
                // added already carry their own; this just backfills older
                // ones by resolving chatUserId (Chainlit's internal user id,
                // not a name) to a display name.
                const ids = Array.from(
                    new Set(
                        configs
                            .filter((c) => !c.ownerDisplayName)
                            .map((c) => c.chatUserId)
                            .filter(Boolean)
                    )
                );
                if (ids.length > 0) {
                    apiClient
                        .get(
                            `/project/users/display-names?ids=${encodeURIComponent(
                                ids.join(',')
                            )}`
                        )
                        .then((res) => res.json())
                        .then((data) => setOwnerNames(data?.names || {}))
                        .catch(() => setOwnerNames({}));
                }
            })
            .catch(() => {
                setLiveConfigs([]);
            })
            .finally(() => setLiveLoading(false));
    }, [mgmtApi, apiClient]);

    const allAgents: DisplayBankAgent[] = useMemo(
        () =>
            liveConfigs.map((config) =>
                liveToDisplay(
                    config,
                    config.chatUserId === user?.id,
                    subscriberCounts[config.id] ?? 0,
                    ownerNames[config.chatUserId]
                )
            ),
        [liveConfigs, user?.id, subscriberCounts, ownerNames]
    );

    const filtered = allAgents.filter((agent) => {
        if (filter === 'Subscribed' && !subscribed.has(agent.id)) return false;
        if (filter === 'Mine' && !agent.isOwner) return false;
        if (query) {
            const q = query.toLowerCase();
            return (
                agent.name.toLowerCase().includes(q) ||
                agent.author.toLowerCase().includes(q) ||
                agent.blurb.toLowerCase().includes(q)
            );
        }
        return true;
    });

    const toggle = (id: string) => {
        const wasSubscribed = subscribed.has(id);

        setSubscribed((prev) => {
            const next = new Set(prev);
            wasSubscribed ? next.delete(id) : next.add(id);
            return next;
        });
        setSubscriberCounts((prev) => ({
            ...prev,
            [id]: Math.max(0, (prev[id] ?? 0) + (wasSubscribed ? -1 : 1))
        }));

        const request = wasSubscribed
            ? apiClient.delete(
                  `/project/deep-intelligence/subscriptions/${id}`,
                  {}
              )
            : apiClient.post('/project/deep-intelligence/subscriptions', {
                  configId: id
              });

        request.catch(() => {
            toast.error(
                wasSubscribed ? 'Failed to unsubscribe' : 'Failed to subscribe'
            );
            // Roll back the optimistic update.
            setSubscribed((prev) => {
                const next = new Set(prev);
                wasSubscribed ? next.add(id) : next.delete(id);
                return next;
            });
            setSubscriberCounts((prev) => ({
                ...prev,
                [id]: Math.max(0, (prev[id] ?? 0) + (wasSubscribed ? 1 : -1))
            }));
        });
    };

    return (
        <div className="bg-background dark:bg-background w-full h-full overflow-y-auto">
            <div className="top-0 z-10 sticky bg-background dark:bg-background/80">
                <MobileNavTrigger />
                {/* Back to feed */}
                <div className="mx-auto px-4 sm:px-12 py-1 pt-8 sm:pt-12 max-w-agentPageWidth">
                    <Button
                        onClick={() => navigate('/scheduled-jobs')}
                        variant="ghost"
                        className="group justify-start -ml-3 px-3 w-fit text-foreground hover:text-foreground"
                    >
                        <span className="relative w-4 h-4 overflow-hidden">
                            <ArrowLeft className="absolute inset-0 transition-transform group-hover:-translate-x-full duration-300 ease-out" />

                            <ArrowLeft className="absolute inset-0 transition-transform translate-x-full group-hover:translate-x-0 duration-300 ease-out" />
                        </span>
                        Back to feed
                    </Button>
                </div>

                <div className="flex justify-between items-start gap-4 mx-auto px-4 sm:px-12 py-4 border-border border-b max-w-agentPageWidth">
                    <div className="flex flex-col gap-1 min-w-0">
                        <h1 className="font-medium text-foreground text-2xl lg:text-3xl">
                            Scheduled Jobs Bank
                        </h1>
                        <p className="text-muted-foreground text-sm sm:text-lg">
                            Discover and subscribe to scheduled jobs published
                            across your organization
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {!isBasicUser && (
                            <Button
                                className="text-sm"
                                variant="primary"
                                size="default"
                                onClick={() => navigate('/scheduled-jobs/new')}
                            >
                                <Plus />
                                Create new job
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="mx-auto px-4 sm:px-12 pt-4 max-w-agentPageWidth">
                <div className="flex items-center gap-3 mb-4">
                    <div className="relative flex-1">
                        <Search className="top-1/2 left-3 absolute size-4 sm:size-5 text-muted-foreground -translate-y-1/2" />
                        <Input
                            type="text"
                            placeholder="Search jobs by name, team or topic…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="pl-9 sm:pl-10 h-9 sm:h-10"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                    {FILTERS.filter((f) => f !== 'Mine' || !isBasicUser).map(
                        (f) => (
                            <Button
                                pill
                                key={f}
                                onClick={() => setFilter(f)}
                                variant={
                                    filter === f ? 'secondary' : 'tertiary'
                                }
                            >
                                {f}
                            </Button>
                        )
                    )}
                </div>

                {liveLoading ? (
                    <div
                        className="items-start gap-4.5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
                        aria-busy="true"
                        aria-label="Loading live configurations"
                    >
                        {Array.from({ length: 6 }, (_, index) => (
                            <Skeleton
                                key={index}
                                className="rounded-xl h-44"
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="py-16 text-muted-foreground text-sm text-center">
                        No live configurations found. Create one to see it here.
                    </div>
                ) : (
                    <div className="items-start gap-4.5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
                        {filtered.map((agent) => (
                            <BankCard
                                key={agent.id}
                                agent={agent}
                                isSubscribed={subscribed.has(agent.id)}
                                onToggle={() => toggle(agent.id)}
                                onNavigate={() =>
                                    navigate(
                                        `/scheduled-jobs/stream/${agent.id}`
                                    )
                                }
                                onManage={() =>
                                    navigate(
                                        `/scheduled-jobs/manage/${agent.id}`
                                    )
                                }
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
